import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import styled, { css } from 'styled-components';

import { InfiniteData, UseInfiniteQueryResult } from '@tanstack/react-query';

import MoreContentRequester from 'components/MoreContentRequester';
import { ActivityInputValues, defaultActivityInputValues } from 'components/shared/activityInput/useActivityInput';
import Button from 'components/shared/Button';
import AttachmentComponent from 'components/shared/ExternalAttachment';
import FileDropZone from 'components/shared/FileDropZone';
import Upload from 'hooks/useDirectUpload/Upload';
import useUpload from 'hooks/useDirectUpload/useDirectUpload';
import Attachment from 'models/Attachment';
import { EscalationTypes } from 'models/Escalation';
import Profile, { PERMISSIONS } from 'models/Profile';
import { TaggableUserResource, taggableUsersOnLocationEpisode } from 'services/api/taggableUsers';
import { useToastActions } from 'stores/toastStore';
import { colors } from 'styles/theme/colors';
import AttachFileIcon from 'svg/AttachFileIcon';

import EpisodeScopedPriorityCheckbox from './EpisodeScopedPriorityCheckbox';
import IconButton from './IconButton';
import { TaggableTextArea } from './TaggableTextArea';
import TaggableUserMenu, { TaggableUserMenuItemProps } from './TaggableUserMenu';

type TextAreaCustomStyles = { minHeight?: string; maxHeight?: string };

type ActivityInputProps = {
  showPostButton?: boolean;
  showEscalate?: boolean;
  showPriority?: boolean;
  taggable?: boolean;
  /** Gates both the Attach button AND the drop target. `false` means a file cannot reach
   * this composer by any route, and a file dropped on it is refused out loud — the clinical
   * assessment notes tab relies on both. */
  attachable?: boolean;
  /** Whether the caller has somewhere to post to. Defaults to "there is a location episode",
   * which is what every caller meant before a workflow-action note existed — that has no
   * location episode and still needs to post. */
  canPost?: boolean;
  /** Submit button copy. The clinical assessment Notes tab says "Post Note". */
  postLabel?: string;
  submitting?: boolean;
  escalationType?: EscalationTypes;

  locationEpisodeId?: string;
  heightSettings?: TextAreaCustomStyles;
  placeholder?: string;
  profile?: Profile | null; //needed if you aren't overriding note creation
  darkBorder?: boolean;

  /** Which resource's participants can be tagged. Defaults to the location
   * episode identified by `locationEpisodeId`. */
  taggableUserResource?: TaggableUserResource;
  values?: ActivityInputValues;
  setValues: React.Dispatch<React.SetStateAction<ActivityInputValues>>;
  onSubmit?: () => void;
  onUploading?: (uploading: boolean) => void;
  /** Fires whenever the uploads list changes (add, remove, progress-driven
   * replacement). Lets a parent track how many uploads are visible as chips,
   * independent of form state — used by the referral modal to verify the
   * submit payload matches what the user sees (OLIO-2638). */
  onUploadsChange?: (uploads: Upload[]) => void;
  existingAttachmentsQuery?: UseInfiniteQueryResult<InfiniteData<any, unknown>, Error>;
  /** Render-only rows shown at the bottom of the attachment list, after
   * any real attachments/uploads. Used for synthetic placeholders (e.g.
   * the auto-included CCD on a referral) that are NOT part of the user's
   * actual `values.attachments` array. */
  staticAttachments?: React.ReactNode;
  /** Rendered in the action bar, immediately before the submit button. For a caller-owned control
   * that ActivityInput has no business knowing about — the controls already in that bar are
   * episode-scoped, and a fourth `show*` boolean would mean teaching this component about a
   * surface's own concerns again. */
  actionsSlot?: React.ReactNode;
};

export default function ActivityInput(props: ActivityInputProps) {
  const {
    attachable = true,
    taggable = true,
    submitting = false,
    darkBorder = true,
    postLabel = 'Post',
    placeholder,
    locationEpisodeId,
    canPost = !!locationEpisodeId,
    showPostButton = false,
    showEscalate = false,
    showPriority = false,
    taggableUserResource,
    heightSettings,
    onSubmit,
    values = defaultActivityInputValues,
    setValues,
    onUploading,
    onUploadsChange,
    existingAttachmentsQuery,
    staticAttachments,
    actionsSlot,
  } = props;

  const { addToast } = useToastActions();
  const taggableTextAreaRef = useRef<any>(null);

  // Tagging is scoped to a single resource. Callers that tag on a review pass
  // it explicitly; everyone else tags on the location episode they're posting
  // to. Memoized on the identifiers so an inline prop object doesn't churn the
  // query key on every render.
  const { resourceType, resourceId } = taggableUserResource ?? {};
  const resource = useMemo<TaggableUserResource | undefined>(() => {
    if (resourceType && resourceId) return { resourceType, resourceId };

    return taggableUsersOnLocationEpisode(locationEpisodeId);
  }, [resourceType, resourceId, locationEpisodeId]);

  // Separate existing Attachment objects from Upload objects
  const existingAttachments = values.attachments.filter((a): a is Attachment => a instanceof Attachment);
  const uploadAttachments = values.attachments.filter((a): a is Upload => a instanceof Upload);
  const { uploadFiles, uploads, uploading, removeUpload, resetUploads, setDocType } = useUpload(uploadAttachments);

  // Sync the hook's `uploads` list back into form state whenever it changes.
  // The existing (non-Upload) attachments MUST be read from `prev` inside the
  // functional updater rather than the render-scope `existingAttachments`
  // variable: this effect's only dep is `uploads`, so closing over the
  // render-time `existingAttachments` captured a stale snapshot and could
  // clobber carried-over / CCD attachments that changed without `uploads`
  // changing (OLIO-2585 — silently dropped referral attachments).
  useEffect(() => {
    setValues((prev) => {
      const prevExisting = prev.attachments.filter((a): a is Attachment => a instanceof Attachment);
      return { ...prev, attachments: [...prevExisting, ...uploads] };
    });
    onUploadsChange?.(uploads);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploads]);

  useEffect(() => {
    onUploading?.(uploading);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploading]);

  const handleRemoveExistingAttachment = useCallback(
    (id: string) => {
      setValues((prev) => ({
        ...prev,
        attachments: prev.attachments.filter((a) => a.id !== id),
      }));
    },
    [setValues]
  );

  const handleTaggableUserMenuItemClick = useCallback(
    (user: TaggableUserMenuItemProps) => {
      taggableTextAreaRef.current?.focus();
      setValues((prev) => {
        const includeSpace = prev.text.length !== 0 && !prev.text.match(/\s$/);
        const userMention = `@{{${user.display}||${user.id}}}`;
        const newText = prev.text + (includeSpace ? ' ' : '') + userMention;
        const newPlaintext = prev.plaintext + (includeSpace ? ' ' : '') + `@${user.display.split(' - ')[0]}`;

        const newMention = {
          id: user.id,
          display: user.display,
          index: prev.text.length + (includeSpace ? 1 : 0),
          plainTextIndex: prev.plaintext.length + (includeSpace ? 1 : 0),
          childIndex: prev.mentions.length,
        };

        return {
          ...prev,
          text: newText,
          plaintext: newPlaintext,
          mentions: [...prev.mentions, newMention],
        };
      });
    },
    [setValues]
  );

  const handleSubmit = useCallback(async () => {
    if (uploads.some((upload) => upload.hasError)) {
      addToast({ text: 'Please remove and re-upload the file(s) that failed' });
      return;
    }

    await onSubmit?.();
    resetUploads();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onSubmit, resetUploads, uploads]);

  // `noDrag` makes react-dropzone attach no drag handlers at all, which leaves the browser's
  // own behavior in charge: a dropped file navigates the tab to it, taking the page — and any
  // typed-but-unposted draft — with it. So when files are refused, refuse them here instead of
  // ignoring the drop. Cancelling `dragenter`/`dragover` is what makes `drop` fire on this
  // element rather than falling through to the browser.
  const blockDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();

      if (event.type === 'drop') {
        addToast({ text: 'Files cannot be attached to a note.' });
      }
    },
    [addToast]
  );

  const dropBlockers = attachable ? {} : { onDragEnter: blockDrop, onDragOver: blockDrop, onDrop: blockDrop };

  const overlayText = useCallback((valid: boolean) => (valid ? 'Drag your files here to attach' : 'Invalid files'), []);
  const disablePost = uploading || !showPostButton || !canPost || !(values.text || values.attachments.length);

  return (
    // `attachable` gates the drop target as well as the Attach button — hiding the button
    // alone would still let a dragged file upload and attach itself.
    <FileDropZone
      onFilesSelected={attachable ? uploadFiles : () => {}}
      currentFileCount={uploads.length}
      noDrag={!attachable}
      noClick>
      {(dropzone) => (
        <Container
          data-testid='activity-input'
          $darkBorder={darkBorder}
          {...dropzone.getRootProps({
            $isDragAccept: dropzone.isDragAccept,
            $isDragActive: dropzone.isDragActive,
            $isDragReject: dropzone.isDragReject,
          })}
          // After `getRootProps` on purpose: these have to win when files are refused.
          {...dropBlockers}>
          <input {...dropzone.getInputProps()} />
          <Overlay $isDragActive={dropzone.isDragActive}>
            <OverlayTitle>{overlayText(dropzone.isDragAccept)}</OverlayTitle>
          </Overlay>
          <TextAreaWrapper $textarea={heightSettings}>
            <TaggableTextArea
              values={values}
              onChange={(values) => setValues((prev) => ({ ...prev, ...values }))}
              placeholder={placeholder}
              disabled={submitting}
              inputRef={taggableTextAreaRef}
              taggable={taggable}
              taggableUserResource={resource}
            />
            <FilesContainer $scrollable={!!existingAttachmentsQuery}>
              {existingAttachments.map((attachment) => (
                <AttachmentComponent
                  disabled={false}
                  key={attachment.id}
                  attachment={attachment}
                  onDeleteAttachment={handleRemoveExistingAttachment}
                />
              ))}
              {existingAttachmentsQuery && <MoreContentRequester resource={existingAttachmentsQuery} />}
              {uploads.map((upload) => (
                <AttachmentComponent
                  disabled={false}
                  error={upload.hasError}
                  key={upload.id}
                  uploadProgress={upload.progress}
                  attachment={upload}
                  onDeleteAttachment={removeUpload}
                  onDocumentTypeChange={setDocType}
                />
              ))}
              {staticAttachments}
            </FilesContainer>
          </TextAreaWrapper>
          <ActionsBar $darkBorder={darkBorder} data-cy='activity-input-actions'>
            {taggable && (
              <TaggableUserMenu taggableUserResource={resource} onMenuItemClick={handleTaggableUserMenuItemClick} />
            )}

            {attachable && (
              <IconButton
                size='xsmall'
                variant='ghost'
                disabled={uploads.length >= 10}
                onClick={dropzone.open}
                type='button'>
                <AttachFileIcon />
                <span>Attach</span>
              </IconButton>
            )}

            {showEscalate && (
              <EpisodeScopedPriorityCheckbox
                permission={PERMISSIONS.escalationCreate}
                label='Escalate'
                values={values}
                setValues={setValues}
              />
            )}

            {showPriority && (
              <EpisodeScopedPriorityCheckbox
                permission={PERMISSIONS.priorityNoteCreate}
                label='Mark as priority'
                values={values}
                setValues={setValues}
              />
            )}
            {actionsSlot}
            {showPostButton && (
              <SubmitButton
                data-testid='activity-input-post'
                disabled={disablePost}
                onClick={handleSubmit}
                loading={submitting}>
                {postLabel}
              </SubmitButton>
            )}
          </ActionsBar>
        </Container>
      )}
    </FileDropZone>
  );
}

const TextAreaWrapper = styled.div<{ $textarea?: TextAreaCustomStyles }>`
  padding: 16px;
  box-sizing: border-box;
  min-height: ${({ $textarea }) => $textarea?.minHeight || '120px'};
  ${({ $textarea }) => {
    if ($textarea?.maxHeight) {
      return css`
        max-height: ${$textarea.maxHeight};
        overflow-y: scroll;
      `;
    }
    return null;
  }};
  display: flex;
  flex-direction: column;
`;

const Container = styled.div<{ $darkBorder: boolean }>`
  background-color: white;
  box-sizing: border-box;
  border-radius: ${({ theme }) => theme.dimensions.borderRadius};
  border-width: 1px;
  border-style: solid;
  border-color: ${({ $darkBorder }) => ($darkBorder ? 'var(--black-25)' : 'var(--black-10)')};
  position: relative;
  display: flex;
  flex-direction: column;

  &:focus-within {
    border-color: var(--primary-blue);
  }

  &:focus-visible {
    outline: none;
  }
`;

const ActionsBar = styled.div<{ $darkBorder: boolean }>`
  display: flex;
  align-items: center;
  border-top: 1px solid ${({ $darkBorder }) => ($darkBorder ? 'var(--black-25)' : 'var(--black-10)')};
  padding: 8px 16px;
  gap: 16px;
`;

const Overlay = styled.div<{ $isDragActive: boolean }>`
  position: absolute;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background-color: ${() => colors.black50};
  border-radius: ${({ theme }) => theme.dimensions.borderRadius};
  display: ${({ $isDragActive }) => ($isDragActive ? 'flex' : 'none')};
`;

const OverlayTitle = styled.h3`
  color: ${() => colors.white};
`;

const SubmitButton = styled(Button)`
  padding: 3px 18px;
  line-height: 1.5;
  border: 2px solid transparent;
  font-size: 10px;
  line-height: 14px;
  margin-left: auto;
`;

const FilesContainer = styled.div<{ $scrollable?: boolean }>`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  flex-direction: column;
  margin-top: 1rem;
  gap: 0.5rem;

  ${({ $scrollable }) =>
    $scrollable &&
    css`
      display: block;
      max-height: 408px;
      overflow-y: auto;
      gap: unset;

      > * + * {
        margin-top: 8px;
      }
    `}
`;
